import request from '@/modules/base/util/request'
import config from '@/config'
import { APIResponse, ResponseBody } from '@/modules/base/util/requestTypes'

const baseUrl = `${config.resourceHost}`

export interface ITenantBankAccount {
  id?: string;
  iban: string;
  bic?: string;
  accountHolder: string;
  description?: string;
  bookingId?: string;
}

export interface TenantBankAccountData {
  id: string | null;
  accountHolder: string | null;
  iban: string | null;
  bic: string | null;
  description: string | null;
  bookingId: string | null;
  contactType?: string | null;
  bankName?: string | null;
  bankLogoSmall?: string | null;
}

export interface IBankAccount {
  accountName: string;
  id: string;
}

export interface IEmailTemplateItem {
  subject: string;
  content: string;
  button: string;
}

export interface IEmailTemplate {
  cancelled: IEmailTemplateItem;
  pending: IEmailTemplateItem;
  activated: IEmailTemplateItem;
}

/** Literal-union of allowed status labels */
export type IEmailStatus = 'cancelled' | 'pending' | 'activated';

export interface ILeadDaysItem {
  max: number;
  min: number;
  days: number;
}

export interface ILeadDays {
  FNAL: ILeadDaysItem;
  FRST: ILeadDaysItem;
  OOFF: ILeadDaysItem;
  RCUR: ILeadDaysItem;
}

export interface CreditorIdentifierData {
  creditorIdentifier: string;
  bankAccountId: string;
  emailTemplate: Record<IEmailStatus, IEmailTemplate>;
  bankAccount: IBankAccount
  leadDays: ILeadDays
  purposeOfUseText: string
  cutOffTime: string
}

type BankAccountResponse = ResponseBody<TenantBankAccountData>;
type CreditorIdentifierResponse = ResponseBody<CreditorIdentifierData>;
type BankAccountListDirectDebitSettingResponse = ResponseBody<IBankAccount[]>;

export const TenantBankAccountService = {
  getCreditorIdentifierInfo: (includeBankAccount = true) => {
    return request(`${baseUrl}setting/creditor-identifier?includeBankAccount=${includeBankAccount}`, {
      method: 'GET',
      auth: true,
      isConcurrent: true,
    })
      .then((response: APIResponse<CreditorIdentifierResponse>) => {
        return response.body.data as CreditorIdentifierData
      })
  },
  getBankAccountListDirectDebitSetting: () => {
    // doppelt?
    return request(`${baseUrl}banking/directDebit/bank-account-list-direct-debit-setting`, {
      method: 'GET',
      auth: true,
      isConcurrent: true,
    })
      .then((response: APIResponse<BankAccountListDirectDebitSettingResponse>) => {
        return response.body.data as IBankAccount[]
      })
  },
  updateCreditorIdentifier: (creditorIdentifier) => {
    return request(`${baseUrl}setting/creditor-identifier`, {
      method: 'POST',
      auth: true,
      isConcurrent: true,
      data: creditorIdentifier,
    })
      .then((response: APIResponse<CreditorIdentifierResponse>) => {
        return response.body.data as CreditorIdentifierData
      })
  },
  createDefault: (data: ITenantBankAccount): Promise<TenantBankAccountData> => {
    return new Promise((resolve, reject) => {
      request(`${baseUrl}tenantBankAccount/default`, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        data,
      })
        .then((response: APIResponse<BankAccountResponse>) => {
          resolve(response.body.data)
        })
        .catch(error => reject(error))
    })
  },
  create: (data: ITenantBankAccount): Promise<TenantBankAccountData> => {
    return new Promise((resolve, reject) => {
      request(`${baseUrl}tenantBankAccount`, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        data,
      })
        .then((response: APIResponse<BankAccountResponse>) => {
          resolve(response.body.data)
        })
        .catch(error => reject(error))
    })
  },
  getDefault: (): Promise<TenantBankAccountData> => {
    return new Promise((resolve, reject) => {
      request(`${baseUrl}tenantBankAccount/default`, {
        method: 'GET',
        auth: true,
        isConcurrent: true,
      })
        .then((response: APIResponse<BankAccountResponse>) => {
          resolve(response.body.data)
        })
        .catch(error => reject(error))
    })
  },
  getAll: (params?: {tenantBankAccountId: string}): Promise<TenantBankAccountData[]> => {
    return new Promise((resolve, reject) => {
      const getAllUrl = `${baseUrl}tenantBankAccount`
      const { tenantBankAccountId } = params || {}
      const query = tenantBankAccountId ? `?tenantBankAccountId=${tenantBankAccountId}` : ''
      const url = getAllUrl + (params ? query : '')

      request(url, {
        method: 'GET',
        auth: true,
        isConcurrent: true,
      })
        .then((response: APIResponse<ResponseBody<TenantBankAccountData[]>>) => {
          resolve(response.body.data)
        })
        .catch(error => reject(error))
    })
  },
  update: (id: string, data: ITenantBankAccount): Promise<TenantBankAccountData> => {
    return new Promise((resolve, reject) => {
      request(`${baseUrl}tenantBankAccount/${id}`, {
        method: 'PUT',
        data,
        auth: true,
        isConcurrent: true,
      })
        .then((response: APIResponse<BankAccountResponse>) => {
          resolve(response.body.data)
        })
        .catch(error => reject(error))
    })
  },
  delete: (id: string): Promise<BankAccountResponse> => {
    return new Promise((resolve, reject) => {
      request(`${baseUrl}tenantBankAccount/${id}`, {
        method: 'DELETE',
        auth: true,
        isConcurrent: true,
      })
        .then((response: BankAccountResponse) => {
          resolve(response)
        })
        .catch(error => reject(error))
    })
  },
}

export default TenantBankAccountService
